import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const EMOJI_ICONS = ["🏡", "✈️", "📚", "🌙", "🎨", "🍷", "🏢", "🌊", "⭐", "🎬", "💎", "🔥"];
const COLOR_SWATCHES = [
  { name: "Amber", value: "#F5A524" },
  { name: "Teal", value: "#14B8A6" },
  { name: "Crimson", value: "#EF4444" },
  { name: "Violet", value: "#8B5CF6" },
  { name: "Emerald", value: "#10B981" },
  { name: "Sky", value: "#0EA5E9" },
  { name: "Rose", value: "#F43F5E" },
  { name: "Orange", value: "#F97316" },
];

export default function NewWorkspaceDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(EMOJI_ICONS[0]);
  const [color, setColor] = useState(COLOR_SWATCHES[0].value);
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setIcon(EMOJI_ICONS[0]); setColor(COLOR_SWATCHES[0].value); setWebsite("");
    }
  }, [open]);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces", {
        name: name.trim(), icon, accent_color: color,
        company_name: name.trim(), website,
      });
      await api.post(`/workspaces/${data.id}/activate`);
      toast.success(`Workspace ${data.name} created`);
      onCreated?.(data);
      onOpenChange(false);
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 sm:max-w-md" data-testid="new-workspace-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">New workspace</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Each workspace has its own branding, projects and analytics.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">NAME</Label>
            <Input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="TravelOthers"
              data-testid="new-ws-name"
              className="mt-2 bg-[#0b0b0d] border-white/10 h-11 focus-visible:border-amber-500"
              autoFocus />
          </div>

          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">ICON</Label>
            <div className="mt-2 grid grid-cols-6 gap-2">
              {EMOJI_ICONS.map((e) => (
                <button
                  key={e} type="button" onClick={() => setIcon(e)}
                  data-testid={`ws-icon-${e}`}
                  className={`h-10 rounded-md border text-xl transition-colors ${
                    icon === e ? "border-amber-500 bg-amber-500/10" : "border-white/10 hover:border-white/20 bg-[#0b0b0d]"
                  }`}>
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">ACCENT COLOR</Label>
            <div className="mt-2 grid grid-cols-8 gap-2">
              {COLOR_SWATCHES.map((s) => (
                <button
                  key={s.value} type="button" onClick={() => setColor(s.value)}
                  data-testid={`ws-color-${s.name.toLowerCase()}`}
                  title={s.name}
                  className={`h-10 rounded-md border-2 transition-transform ${
                    color === s.value ? "border-white scale-110" : "border-white/10"
                  }`}
                  style={{ backgroundColor: s.value }} />
              ))}
            </div>
          </div>

          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">WEBSITE · OPTIONAL</Label>
            <Input
              value={website} onChange={(e) => setWebsite(e.target.value)}
              placeholder="travelothers.com"
              data-testid="new-ws-website"
              className="mt-2 bg-[#0b0b0d] border-white/10 h-11" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400">Cancel</Button>
          <Button onClick={create} disabled={busy || !name.trim()}
            data-testid="new-ws-create"
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Create workspace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
